import { useCallback } from 'react';

import { GOOGLE_RECAPTCHA_SITE_KEY } from '~/constants';
import { log } from '~/util';

export const RECAPTCHA_CONTAINER_ID = 'google-recaptcha-container';

// Strings must stay in sync with pkg/recaptcha/actions.go.
export enum RecaptchaAction {
  Login = 'login',
  MagicLink = 'magic_link',
  Register = 'register',
  PasswordResetRequest = 'password_reset_request',
  PasswordReset = 'password_reset',
  MfaSetup = 'mfa_setup',
  UpdateUser = 'update_user',
  Contact = 'contact',
}

const BADGE_ACTION = 'badge';

type ReCaptchaRenderParameters = {
  sitekey: string;
  action: string;
  badge: 'inline' | 'bottomleft' | 'bottomright';
  size: 'invisible';
};

type ReCaptchaExecute = {
  (widgetId: number): Promise<string>;
  (siteKey: string, options: { action: string }): Promise<string>;
};

type ReCaptchaEnterprise = {
  ready: (callback: () => void) => void;
  render: (container: string | HTMLElement, parameters: ReCaptchaRenderParameters) => number;
  execute: ReCaptchaExecute;
};

declare global {
  interface Window {
    grecaptcha?: {
      enterprise?: ReCaptchaEnterprise;
    };
  }
}

let badgeRendered = false;

const getReCaptchaEnterprise = async (): Promise<ReCaptchaEnterprise> => {
  const enterprise = window.grecaptcha?.enterprise;
  if (!enterprise) {
    throw new Error('reCaptcha not loaded');
  }

  await new Promise<void>((resolve) => enterprise.ready(resolve));

  return enterprise;
};

const ensureBadgeRendered = async (siteKey: string): Promise<void> => {
  if (badgeRendered) {
    return;
  }

  if (!siteKey) {
    throw new Error('reCaptcha site key not configured');
  }

  const container = document.getElementById(RECAPTCHA_CONTAINER_ID);
  if (!container) {
    throw new Error('reCaptcha container not found');
  }

  const enterprise = await getReCaptchaEnterprise();

  enterprise.render(container, {
    sitekey: siteKey,
    action: BADGE_ACTION,
    badge: 'inline',
    size: 'invisible',
  });

  badgeRendered = true;
};

// A failed mint resolves with a token rather than rejecting, and the server rejects it as BROWSER_ERROR.
// Google documents no format, but every such token observed in prod starts "HF" and every accepted one
// starts "0c". Only "HF" retries, so a format change costs the retry rather than retrying every mint.
const MINT_ERROR_TOKEN_PREFIX = 'HF';
const MINT_ATTEMPTS = 3;

// Failures are independent, so retrying clears most of them. Exported for tests.
export const mintWithRetry = async (mint: () => Promise<string>, action: string): Promise<string> => {
  let token = await mint();
  for (let attempt = 1; attempt < MINT_ATTEMPTS && token.startsWith(MINT_ERROR_TOKEN_PREFIX); attempt++) {
    log({ name: 'reCaptcha mint retried', type: 'warn', context: { action, attempt } });
    token = await mint();
  }

  return token;
};

const executeReCaptcha = async (siteKey: string, action: RecaptchaAction): Promise<string> => {
  await ensureBadgeRendered(siteKey);
  const enterprise = await getReCaptchaEnterprise();

  return mintWithRetry(() => enterprise.execute(siteKey, { action }), action);
};

type ReCaptchaConfig = { optional?: boolean };

// optional: true resolves to undefined instead of rejecting, for endpoints the server assesses but does
// not enforce, so a network that breaks reCAPTCHA cannot block the action.
type ReCaptchaExecutor = {
  (action: RecaptchaAction, config: { optional: true }): Promise<string | undefined>;
  (action: RecaptchaAction, config?: { optional?: false }): Promise<string>;
};

export const useReCAPTCHA = (): ReCaptchaExecutor => {
  return useCallback(
    ((action: RecaptchaAction, config?: ReCaptchaConfig) =>
      executeReCaptcha(GOOGLE_RECAPTCHA_SITE_KEY, action).catch((error: unknown) => {
        log({ name: 'reCaptcha error', type: 'error', context: { error } });
        if (config?.optional) {
          return undefined;
        }
        const message = error instanceof Error ? error.message : error;
        if (message === 'Challenge cancelled by user.') {
          throw new Error('reCAPTCHA challenge not completed');
        }
        throw new Error('reCAPTCHA error');
      })) as ReCaptchaExecutor,
    [GOOGLE_RECAPTCHA_SITE_KEY]
  );
};
